import { useState, useRef, useMemo, useEffect } from 'react';
import { MdClose } from 'react-icons/md';
import type { StorefrontProducto } from '@almacenes/core';
import { toTitleCase, formatPrecio, BRAND } from '../utils';
import { useFocusTrap } from '../hooks/use-focus-trap';

interface Props {
  producto: StorefrontProducto;
  cantidadActual: number;
  onConfirmar: (cantidad: number, subtotal: number) => void;
  onCerrar: () => void;
}

export const ModalGranel = ({ producto, cantidadActual, onConfirmar, onCerrar }: Props) => {
  const [modo, setModo] = useState<'kg' | 'monto'>('kg');
  const [input, setInput] = useState(cantidadActual > 0 ? String(cantidadActual) : '');
  const [error, setError] = useState('');
  const [confirmarAlta, setConfirmarAlta] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useFocusTrap(dialogRef, onCerrar);

  useEffect(() => { inputRef.current?.focus(); }, [modo]);
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const precio = producto.precioVenta;

  const kgsEquivalente = useMemo(() => {
    if (modo !== 'monto') return null;
    const monto = parseFloat(input);
    if (isNaN(monto) || monto <= 0 || precio <= 0) return null;
    return Math.round((monto / precio) * 1000) / 1000;
  }, [input, modo, precio]);

  const subtotalPreview = useMemo(() => {
    if (modo === 'kg') {
      const kg = parseFloat(input.replace(',', '.'));
      if (isNaN(kg) || kg <= 0) return null;
      return Math.round(kg * precio);
    }
    const monto = parseFloat(input);
    if (isNaN(monto) || monto <= 0) return null;
    return Math.round(monto);
  }, [input, modo, precio]);

  const handleInput = (val: string) => {
    if (modo === 'monto' && (!/^\d*$/.test(val) || val.length > 9)) return;
    setInput(val);
    setError('');
    setConfirmarAlta(false);
  };

  const ejecutarConfirmar = () => {
    const valor = parseFloat(input.replace(',', '.'));
    if (isNaN(valor) || valor <= 0) {
      setError(modo === 'kg' ? 'Ingresa una cantidad válida en kg' : 'Ingresa un monto válido');
      return;
    }

    let cantidad: number;
    let subtotal: number;

    if (modo === 'kg') {
      cantidad = Math.round(valor * 1000) / 1000;
      subtotal = Math.round(cantidad * precio);
    } else {
      subtotal = Math.round(valor);
      cantidad = Math.round((valor / precio) * 100000) / 100000;
      if (cantidad < 0.001) {
        setError('El monto es insuficiente para el precio por kg');
        return;
      }
    }

    if (producto.stock != null && cantidad > producto.stock) {
      setError(`Solo hay ${producto.stock} kg disponibles`);
      return;
    }

    if (cantidad > 5 && !confirmarAlta) {
      setConfirmarAlta(true);
      return;
    }

    onConfirmar(cantidad, subtotal);
  };

  const handleConfirmar = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(ejecutarConfirmar, 300);
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-granel-titulo"
    >
      <div
        className="absolute inset-0 bg-black/50"
        style={{ animation: 'fadeIn 200ms ease' }}
        onClick={onCerrar}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        className="relative bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl shadow-2xl p-5 flex flex-col gap-4"
        style={{ animation: 'slideInUp 300ms cubic-bezier(0.32,0.72,0,1)' }}
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 id="modal-granel-titulo" className="text-label" style={{ color: BRAND.navy }}>
              {toTitleCase(producto.nombre)}
            </h2>
            <p className="text-small text-gray-500 mt-0.5">
              {formatPrecio(precio)}<span className="text-gray-400">/kg</span>
            </p>
          </div>
          <button
            onClick={onCerrar}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 -mr-1"
            aria-label="Cerrar"
          >
            <MdClose className="text-xl" aria-hidden="true" />
          </button>
        </div>

        <div className="flex rounded-xl overflow-hidden border border-gray-200" role="group" aria-label="Modo de ingreso">
          {(['kg', 'monto'] as const).map((m) => (
            <button
              key={m}
              onClick={() => { setModo(m); setInput(''); setError(''); setConfirmarAlta(false); }}
              className="flex-1 py-2 text-sm font-medium transition-colors"
              style={modo === m ? { backgroundColor: BRAND.cyan, color: '#fff' } : { backgroundColor: '#fff', color: '#6B7280' }}
              aria-pressed={modo === m}
            >
              {m === 'kg' ? 'Ingresar kg' : 'Ingresar monto ($)'}
            </button>
          ))}
        </div>

        <div>
          <label htmlFor="input-granel" className="text-small text-gray-600 block mb-1">
            {modo === 'kg' ? 'Cantidad en kilogramos' : 'Monto en pesos'}
          </label>
          <input
            ref={inputRef}
            id="input-granel"
            type="text"
            inputMode={modo === 'monto' ? 'numeric' : 'decimal'}
            placeholder={modo === 'kg' ? 'Ej: 0.5' : 'Ej: 1000'}
            value={input}
            onChange={(e) => handleInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleConfirmar(); } }}
            className="w-full border border-gray-200 rounded-xl px-3 py-3 text-lg font-semibold focus:outline-none focus:ring-2 text-center"
            style={{ '--tw-ring-color': BRAND.cyan, color: BRAND.navy } as React.CSSProperties}
            aria-describedby={error ? 'granel-error' : undefined}
          />
          {error && (
            <p id="granel-error" className="text-small mt-1" style={{ color: '#EF4444' }} role="alert">
              {error}
            </p>
          )}
        </div>

        {confirmarAlta && (
          <div
            className="rounded-xl p-3 text-sm text-center"
            style={{ backgroundColor: '#FFF7ED', color: BRAND.orange }}
            role="alert"
          >
            ¿Confirmar{' '}
            <strong>
              {modo === 'kg' ? `${parseFloat(input.replace(',', '.'))} kg` : `≈ ${kgsEquivalente} kg`}
            </strong>
            ?
          </div>
        )}

        {!confirmarAlta && (kgsEquivalente !== null || subtotalPreview !== null) && (
          <div
            className="rounded-xl p-3 flex justify-between text-sm"
            style={{ backgroundColor: '#EAF6FD' }}
            aria-live="polite"
          >
            {modo === 'monto' && kgsEquivalente !== null && (
              <span className="text-gray-600">
                ≈ <strong style={{ color: BRAND.navy }}>{kgsEquivalente} kg</strong>
              </span>
            )}
            {subtotalPreview !== null && (
              <span className="ml-auto font-bold" style={{ color: BRAND.navy }}>
                Total: {formatPrecio(subtotalPreview)}
              </span>
            )}
          </div>
        )}

        <button
          onClick={handleConfirmar}
          className="btn-primary w-full py-3 rounded-xl text-sm font-bold"
          disabled={!input || parseFloat(input) <= 0}
        >
          {confirmarAlta ? 'Sí, confirmar cantidad' : cantidadActual > 0 ? 'Actualizar cantidad' : 'Agregar al carrito'}
        </button>
      </div>
    </div>
  );
};
